import BirthdayHeader from "@/components/birthday-reminder/BirthdayHeader";
import BirthdayFooter from "@/components/birthday-reminder/BirthdayFooter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Shield, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function BirthdayReminderPrivacy() {
  return (
    <div className="min-h-screen flex flex-col">
      <BirthdayHeader currentPath="/birthdayreminder/privacy" />

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          <p className="text-xl text-muted-foreground">
            Your data stays on your device. <span className="font-semibold text-foreground">Always.</span>
          </p>
          <Badge variant="secondary" className="text-sm">
            Last Updated: December 25, 2024
          </Badge>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Introduction */}
          <div className="prose dark:prose-invert max-w-none mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Privacy Promise</h2>
            <p className="text-lg text-muted-foreground">
              Birthday Reminder is built with privacy at its core. We believe your personal data should stay personal. 
              That's why we've designed the app to store everything locally on your device - no servers, 
              no cloud storage, no data collection. Period.
            </p>
          </div>

          {/* Privacy Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {/* What We DON'T Collect */}
            <AccordionItem value="dont-collect" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <span>Information We DO NOT Collect</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p className="font-semibold text-foreground">We do not collect ANY of the following:</p>
                <ul className="space-y-2 list-disc pl-6">
                  <li><strong>Personal Information:</strong> No names, emails, phone numbers, or addresses</li>
                  <li><strong>Usage Analytics:</strong> We don't track how you use the app</li>
                  <li><strong>Device Information:</strong> No device IDs, IP addresses, or location data</li>
                  <li><strong>Birthday Data:</strong> The birthdays you add never leave your device</li>
                  <li><strong>Contact Information:</strong> Imported contacts stay on your device only</li>
                  <li><strong>Photos:</strong> Any photos you add are stored locally</li>
                  <li><strong>Behavioral Data:</strong> No tracking of app interactions or user behavior</li>
                </ul>
                <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-4">
                  <p className="text-green-800 dark:text-green-200 font-semibold">
                    ✓ Zero data collection means complete privacy for you
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* What Is Stored Locally */}
            <AccordionItem value="local-storage" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-blue-500" />
                  <span>Information Stored Locally on Your Device</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p>
                  The following information is stored <strong>exclusively on your device</strong> using secure 
                  local storage provided by iOS and Android:
                </p>
                <ul className="space-y-2 list-disc pl-6">
                  <li><strong>Birthday Information:</strong> Names, dates, photos, and relationship notes</li>
                  <li><strong>Notification Preferences:</strong> Your reminder settings and times</li>
                  <li><strong>App Settings:</strong> Your preferences for how the app works</li>
                  <li><strong>Import History:</strong> Records of which contacts you've imported (to prevent duplicates)</li>
                </ul>
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                  <p className="text-blue-800 dark:text-blue-200">
                    <strong>Important:</strong> This data never leaves your device. It's not sent to any server, 
                    not backed up to our cloud (because we don't have one), and only accessible to you.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Permissions We Request */}
            <AccordionItem value="permissions" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-purple-500" />
                  <span>Permissions We Request & Why</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p>Birthday Reminder requests the following permissions to provide core functionality:</p>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-foreground mb-1">📱 Contacts Permission</h4>
                    <p><strong>Why:</strong> To import birthdays from your device contacts</p>
                    <p><strong>Used For:</strong> One-time or on-demand import of birthday information</p>
                    <p><strong>Privacy:</strong> We only read birthday data; contacts never leave your device</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-foreground mb-1">🔔 Notifications Permission</h4>
                    <p><strong>Why:</strong> To send you birthday reminders</p>
                    <p><strong>Used For:</strong> Local notifications scheduled on your device</p>
                    <p><strong>Privacy:</strong> Notifications are generated locally, not from a server</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-foreground mb-1">📷 Camera & Photos Permission</h4>
                    <p><strong>Why:</strong> To add photos to birthday entries</p>
                    <p><strong>Used For:</strong> Selecting or taking photos to personalize birthdays</p>
                    <p><strong>Privacy:</strong> Photos stay on your device; never uploaded anywhere</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-foreground mb-1">💾 Storage Permission (Android Only)</h4>
                    <p><strong>Why:</strong> To save photos locally</p>
                    <p><strong>Used For:</strong> Storing birthday photos in app storage</p>
                    <p><strong>Privacy:</strong> All data remains local to your device</p>
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mt-4">
                  <p className="text-purple-800 dark:text-purple-200">
                    <strong>You're in control:</strong> You can revoke any permission at any time through your device 
                    settings. Some features may not work without their required permissions.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Data Security */}
            <AccordionItem value="security" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-green-500" />
                  <span>Data Security</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <ul className="space-y-3 list-disc pl-6">
                  <li>
                    <strong>Device-Level Encryption:</strong> Your data is protected by iOS and Android's native 
                    security features, including device encryption
                  </li>
                  <li>
                    <strong>No Network Transmission:</strong> Since data never leaves your device, there's no risk 
                    of interception during transmission
                  </li>
                  <li>
                    <strong>No Third-Party Access:</strong> We don't have access to your data because it's not 
                    stored on our servers
                  </li>
                  <li>
                    <strong>You Control Everything:</strong> Delete the app, and all your data is removed. 
                    No lingering copies on remote servers
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* Your Rights */}
            <AccordionItem value="rights" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-orange-500" />
                  <span>Your Rights & Data Control</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <ul className="space-y-3 list-disc pl-6">
                  <li>
                    <strong>Complete Ownership:</strong> You own all the data in the app - it's yours, not ours
                  </li>
                  <li>
                    <strong>Easy Deletion:</strong> Delete the app from your device to permanently remove all data
                  </li>
                  <li>
                    <strong>No Account Deletion Needed:</strong> Since there are no user accounts, there's nothing 
                    to delete on our end
                  </li>
                  <li>
                    <strong>Device Backups:</strong> Your data may be included in device backups (iCloud, Google Drive) 
                    based on your device settings - this is outside our control
                  </li>
                  <li>
                    <strong>Data Portability:</strong> You can manually export your birthdays by using your device's 
                    contact sharing features
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* Third-Party Services */}
            <AccordionItem value="third-party" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <span>Third-Party Services</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p className="font-semibold text-foreground text-lg">We use ZERO third-party services.</p>
                <ul className="space-y-2 list-disc pl-6">
                  <li>No advertising networks (we don't show ads)</li>
                  <li>No analytics platforms (we don't track you)</li>
                  <li>No social media integrations (no Facebook, Google, etc.)</li>
                  <li>No crash reporting services</li>
                  <li>No backend servers or APIs</li>
                </ul>
                <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-4">
                  <p className="text-green-800 dark:text-green-200">
                    <strong>This means:</strong> Your data isn't shared with, sold to, or processed by any 
                    third parties. Ever.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Children's Privacy */}
            <AccordionItem value="children" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-pink-500" />
                  <span>Children's Privacy (COPPA Compliance)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p>
                  Birthday Reminder is rated for ages 4+ and is safe for children to use because we don't collect 
                  any personal information from anyone, including children.
                </p>
                <ul className="space-y-2 list-disc pl-6">
                  <li>No personal information is collected from users of any age</li>
                  <li>No user accounts or registrations required</li>
                  <li>No communication features with other users</li>
                  <li>No in-app purchases or advertisements</li>
                </ul>
                <p>
                  While the app is safe, we recommend parental guidance for young children to help them use the app 
                  effectively.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Changes to Policy */}
            <AccordionItem value="changes" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-blue-500" />
                  <span>Changes to This Privacy Policy</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p>
                  We may update this Privacy Policy from time to time. If we make significant changes, we will:
                </p>
                <ul className="space-y-2 list-disc pl-6">
                  <li>Update the "Last Updated" date at the top of this page</li>
                  <li>Notify users through an in-app message (if applicable)</li>
                  <li>Post the updated policy on this website</li>
                </ul>
                <p>
                  Your continued use of the app after any changes means you accept the updated Privacy Policy.
                </p>
                <p className="font-semibold text-foreground">
                  However, our core principle will never change: Your data stays on your device.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Contact Information */}
            <AccordionItem value="contact" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-purple-500" />
                  <span>Contact Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground space-y-4 pt-4">
                <p>
                  If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us:
                </p>
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <p><strong>Developer:</strong> Designr</p>
                  <p><strong>Email:</strong> <a href="mailto:developer@designr.co" className="text-primary hover:underline">developer@designr.co</a></p>
                  <p><strong>Website:</strong> <a href="https://designr.co" className="text-primary hover:underline">https://designr.co</a></p>
                </div>
                <p className="text-sm">
                  We typically respond to inquiries within 24-48 hours.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Summary Box */}
          <div className="mt-12 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Privacy Policy Summary</h3>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div>
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <h4 className="font-semibold mb-1">100% Local Storage</h4>
                <p className="text-sm text-muted-foreground">All data stays on your device</p>
              </div>
              <div>
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <h4 className="font-semibold mb-1">Zero Data Collection</h4>
                <p className="text-sm text-muted-foreground">We don't collect anything</p>
              </div>
              <div>
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <h4 className="font-semibold mb-1">You're in Control</h4>
                <p className="text-sm text-muted-foreground">Your data, your device, your choice</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BirthdayFooter />
    </div>
  );
}

